import { Pressable, View } from "@-/com.npmjs.react-native";
import { Icon } from "@-/dev.improve.haniverse.components.ui.icon";
import { useSelectableTextPress } from "@-/dev.improve.haniverse.components.ui.long-pressable";
import { Text } from "@-/dev.improve.haniverse.components.ui.text";
import { toDiacriticPinyin } from "@-/dev.improve.haniverse.lib.morpheme.grapheme.pinyin";
import { useRouteBodyState } from "@-/dev.improve.haniverse.lib.navigation.route-body-state";
import { PinyinPronunciation } from "@-/dev.improve.haniverse.routes.nav.app._components.pinyin-pronunciation";
import type { GraphemeDictionaryReading } from "#use-grapheme-dictionary-readings";

/** Roughly what fits on one line beside the pinyin, grade and chevron on a phone. */
const ONE_LINE_DEFINITION_LENGTH = 28;

/**
 * One pronunciation of the character, laid out like a word surface's sentence row: its pinyin
 * (with audio), the definitions for that reading on one line, and its curriculum grade. The
 * chevron lists every definition.
 */
export function GraphemeDictionaryReadingRow({
  grapheme,
  reading,
  showTopDivider,
}: {
  grapheme: string;
  reading: GraphemeDictionaryReading;
  showTopDivider: boolean;
}) {
  const { definitions, gradeLabel, toneNumbersPinyin } = reading;
  const [expanded, setExpanded] = useRouteBodyState(
    `grapheme-reading:${grapheme}:${toneNumbersPinyin}:expanded`,
    false,
  );
  const pinyin = toDiacriticPinyin(toneNumbersPinyin);
  const joinedDefinitions = definitions.join("; ");
  const expandable =
    definitions.length > 1 || joinedDefinitions.length > ONE_LINE_DEFINITION_LENGTH;
  // Holding the definitions selects their text natively; that hold's release must not toggle them.
  const definitionsPress = useSelectableTextPress(
    expandable ? () => setExpanded(!expanded) : undefined,
  );
  const toggleLabel = `${expanded ? "Collapse" : "Expand"} definitions for ${pinyin}`;

  return (
    <View testID={`grapheme-dictionary-reading-${toneNumbersPinyin}`}>
      {showTopDivider ? <View className="border-t border-border" /> : null}
      <View className="relative min-h-[56px] flex-row items-center gap-x-2 py-2 pr-[54px] pl-3">
        {/* A fixed column keeps every reading's definitions aligned beneath each other. */}
        <View className="min-h-8 w-18 shrink-0 justify-center">
          <PinyinPronunciation
            alignment="start"
            card={{ grapheme, pinyinList: [{ toneNumbersPinyin }] }}
            pinyinList={[pinyin]}
          />
        </View>
        <View className="min-h-8 min-w-0 flex-1 justify-center">
          {definitions.length === 0 ? (
            <Text className="text-sm text-muted-foreground italic">No definitions</Text>
          ) : (
            <Text
              accessibilityState={expandable ? { expanded } : undefined}
              className="text-sm leading-5 text-muted-foreground"
              numberOfLines={expanded ? undefined : 1}
              {...definitionsPress}
              selectable
              testID={expanded ? "grapheme-dictionary-reading-definitions-expanded" : undefined}
            >
              {expanded
                ? definitions.map((definition) => `• ${definition}`).join("\n")
                : joinedDefinitions}
            </Text>
          )}
        </View>
        {gradeLabel === null ? null : (
          <View className="absolute top-2 right-3">
            <Text className="text-right text-xs text-muted-foreground">{gradeLabel}</Text>
          </View>
        )}
        {expandable ? (
          <Pressable
            accessibilityLabel={toggleLabel}
            accessibilityRole="button"
            accessibilityState={{ expanded }}
            className="absolute right-0 bottom-0 items-center justify-center p-[9px]"
            hitSlop={6}
            onPress={() => setExpanded(!expanded)}
          >
            <Icon
              className="text-muted-foreground"
              name={expanded ? "LuChevronUp" : "LuChevronDown"}
              size={18}
            />
          </Pressable>
        ) : null}
      </View>
    </View>
  );
}
